// src/contexts/ShiftContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import shiftService from '../services/shift.service';
import { sessionCache } from '../utils/sessionCache';

const ShiftContext = createContext();

export const useShift = () => {
    const context = useContext(ShiftContext);
    if (!context) {
        throw new Error('useShift must be used within a ShiftProvider');
    }
    return context;
};

export const ShiftProvider = ({ children }) => {
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch all shifts on mount
    useEffect(() => {
        fetchShifts();
    }, []);

    const fetchShifts = useCallback(async (forceRefresh = false) => {
        const cacheKey = 'shifts_list';
        
        // Check cache first if not forcing refresh
        if (!forceRefresh) {
            const cached = sessionCache.get(cacheKey);
            if (cached) {
                setShifts(cached);
                return;
            }
        }

        try {
            setLoading(true);
            setError(null);
            const response = await shiftService.getAllShifts();
            const shiftList = Array.isArray(response) ? response : (response.shifts || response.data || []);
            
            setShifts(shiftList);
            sessionCache.set(cacheKey, shiftList);
        } catch (error) {
            console.error('Error fetching shifts:', error);
            setError(error.message || 'Failed to fetch shifts');
        } finally {
            setLoading(false);
        }
    }, []);

    const createShift = async (shiftData) => {
        try {
            setLoading(true);
            setError(null);
            const response = await shiftService.createShift(shiftData);
            
            if (response.success || response.shift) {
                const newShift = response.shift || response;
                setShifts(prev => [...prev, newShift]);
                sessionCache.set('shifts_list', [...shifts, newShift]);
                return { success: true, shift: newShift };
            } else {
                throw new Error(response.message || 'Failed to create shift');
            }
        } catch (error) {
            console.error('Error creating shift:', error);
            setError(error.message || 'Failed to create shift');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const updateShift = async (shiftId, shiftData) => {
        try {
            setLoading(true);
            setError(null);
            const response = await shiftService.updateShift(shiftId, shiftData);
            
            if (response.success || response.shift) {
                const updatedShift = response.shift || response;
                setShifts(prev => prev.map(shift => 
                    shift.id === shiftId ? updatedShift : shift
                ));
                sessionCache.set('shifts_list', shifts.map(shift => 
                    shift.id === shiftId ? updatedShift : shift
                ));
                return { success: true, shift: updatedShift };
            } else {
                throw new Error(response.message || 'Failed to update shift');
            }
        } catch (error) {
            console.error('Error updating shift:', error);
            setError(error.message || 'Failed to update shift');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const deleteShift = async (shiftId) => {
        try {
            setLoading(true);
            setError(null);
            const response = await shiftService.deleteShift(shiftId);
            
            if (response.success) {
                setShifts(prev => prev.filter(shift => shift.id !== shiftId));
                sessionCache.set('shifts_list', shifts.filter(shift => shift.id !== shiftId));
                return { success: true };
            } else {
                throw new Error(response.message || 'Failed to delete shift');
            }
        } catch (error) {
            console.error('Error deleting shift:', error);
            setError(error.message || 'Failed to delete shift');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const getShiftById = useCallback((shiftId) => {
        return shifts.find(shift => shift.id === shiftId) || null;
    }, [shifts]);

    const getShiftOptions = useCallback(() => {
        return shifts.map(shift => ({
            value: shift.id,
            label: shift.name,
            startTime: shift.start_time,
            endTime: shift.end_time,
            gracePeriod: shift.grace_period || 15
        }));
    }, [shifts]);

    // Check if arrival is on time based on shift
    const checkArrivalTime = useCallback((loginTime, shiftId) => {
        const shift = getShiftById(shiftId);
        if (!shift || !loginTime) {
            // Dev Logger
            console.log('🕐 DEV LOGGER - checkArrivalTime: No shift or loginTime', {
                loginTime,
                shiftId,
                shift: shift ? 'found' : 'not found'
            });
            return { isOnTime: true, status: 'On Time' };
        }

        const [loginHours, loginMinutes] = loginTime.split(':').map(Number);
        const [shiftHours, shiftMinutes] = shift.start_time.split(':').map(Number);
        const gracePeriod = shift.grace_period || 15; // Default 15 minutes grace period

        const loginTimeMinutes = loginHours * 60 + loginMinutes;
        const shiftTimeMinutes = shiftHours * 60 + shiftMinutes;
        const allowedLateTime = shiftTimeMinutes + gracePeriod;

        const isOnTime = loginTimeMinutes <= allowedLateTime;

        // Dev Logger
        console.log('🕐 DEV LOGGER - checkArrivalTime Calculation:', {
            loginTime,
            shiftId,
            shift: {
                name: shift.name,
                start_time: shift.start_time,
                grace_period: shift.grace_period
            },
            calculation: {
                loginTimeMinutes,
                shiftTimeMinutes,
                gracePeriod,
                allowedLateTime,
                isOnTime,
                lateBy: isOnTime ? 0 : loginTimeMinutes - shiftTimeMinutes
            }
        });

        return {
            isOnTime,
            status: isOnTime ? 'On Time' : 'Late',
            lateBy: isOnTime ? 0 : loginTimeMinutes - shiftTimeMinutes
        };
    }, [getShiftById]);

    const value = {
        shifts,
        loading,
        error,
        fetchShifts,
        createShift,
        updateShift,
        deleteShift,
        getShiftById,
        getShiftOptions,
        checkArrivalTime
    };

    return (
        <ShiftContext.Provider value={value}>
            {children}
        </ShiftContext.Provider>
    );
};